import React, { Component } from "react";
import { graphql } from "react-apollo";
import gql from "graphql-tag";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import "./_foundationAnalysis.scss";

// Questions, options and explanations arrive as HTML/MathML — typeset with MathJax
// so the maths actually renders (otherwise only the plain question text shows).
const MATHJAX_CONFIG = {
  loader: { load: ["input/mml", "output/chtml"] },
  options: { enableMenu: false },
};
const Html = ({ html, className }) => (
  <MathJax dynamic inline>
    <span className={className} dangerouslySetInnerHTML={{ __html: html || "" }} />
  </MathJax>
);

/**
 * Real post-test analysis (used by the dashboard "View Analysis").
 * Fetches the attempt's actual questions with the student's answer, the correct
 * answer, the explanation, and similar questions — all from the database.
 */
const ATTEMPT_ANALYSIS = gql`
  query ($attempt_id: Int!) {
    getFoundationAttemptAnalysis(attempt_id: $attempt_id) {
      attempt_id
      score
      total_questions
      duration_seconds
      questions {
        question_id
        order
        question
        options { id text }
        correct_option
        selected_option
        is_correct
        status
        explanation
        subject
        chapter
        topic
        time_taken_seconds
        similar {
          question_id
          question
          options { id text }
          answer
          explanation
        }
      }
    }
  }
`;

const fmt = (s) => {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
};
const STATUS_LABEL = { correct: "Correct", wrong: "Wrong", unattempted: "Not answered" };

class FoundationAttemptAnalysisView extends Component {
  state = { filter: "all", openSimilar: {} };

  toggleSimilar = (qid) =>
    this.setState((s) => ({ openSimilar: { ...s.openSimilar, [qid]: !s.openSimilar[qid] } }));

  renderOptions(options, correct, chosen) {
    return (
      <div className="fan-opts">
        {options.map((op) => {
          const isCorrect = op.id === correct;
          const isChosenWrong = op.id === chosen && op.id !== correct;
          return (
            <div key={op.id} className={`fan-opt ${isCorrect ? "is-correct" : ""} ${isChosenWrong ? "is-wrong" : ""}`}>
              <span className="fan-opt-id">{op.id}</span>
              <Html className="fan-opt-text" html={op.text} />
              {isCorrect ? <span className="fan-tag ok"><i className="fas fa-check" /> Correct</span> : null}
              {isChosenWrong ? <span className="fan-tag bad"><i className="fas fa-times" /> Your answer</span> : null}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { onClose } = this.props;
    const q = this.props.analysis || {};
    const loading = q.loading;
    const data = q.getFoundationAttemptAnalysis;
    const { filter, openSimilar } = this.state;

    const questions = (data && data.questions) || [];
    const visible = filter === "all" ? questions : questions.filter((x) => x.status === filter);
    const counts = {
      all: questions.length,
      correct: questions.filter((x) => x.status === "correct").length,
      wrong: questions.filter((x) => x.status === "wrong").length,
    };

    return (
      <MathJaxContext version={3} config={MATHJAX_CONFIG}>
      <div className="fan-overlay">
        <div className="fan">
          <header className="fan-head">
            <span className="fan-head-icon" aria-hidden="true"><i className="fas fa-chart-pie" /></span>
            <div className="fan-head-text">
              <h3>Test Analysis</h3>
              {data ? (
                <p>Scored {data.score} / {data.total_questions} · {fmt(data.duration_seconds)}</p>
              ) : null}
            </div>
            <button className="fan-close" onClick={onClose} aria-label="Close"><i className="fas fa-times" /></button>
          </header>

          {loading && !data ? (
            <div className="fan-empty fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-spinner fa-pulse" /></div>
              <h4>Loading analysis…</h4>
              <div className="fan-skel">
                <span className="fdn-skel" /><span className="fdn-skel" /><span className="fdn-skel" />
              </div>
            </div>
          ) : !data || !questions.length ? (
            <div className="fan-empty fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-folder-open" /></div>
              <h4>Nothing to show yet</h4>
              <p>No analysis available for this test.</p>
            </div>
          ) : (
            <>
              <div className="fan-filters">
                {[
                  { key: "all", label: "All", n: counts.all },
                  { key: "correct", label: "Correct", n: counts.correct },
                  { key: "wrong", label: "Wrong", n: counts.wrong },
                ].map((f) => (
                  <button
                    key={f.key}
                    className={`fan-filter fdn-chip is-${f.key} ${filter === f.key ? "is-active" : ""}`}
                    onClick={() => this.setState({ filter: f.key })}
                  >
                    {f.label} <em>{f.n}</em>
                  </button>
                ))}
              </div>

              <div className="fan-body">
                {visible.map((qq) => (
                  <article className={`fan-q is-${qq.status}`} key={qq.question_id}>
                    <header className="fan-q-head">
                      <span className="fan-q-no">Q{qq.order}</span>
                      <span className={`fan-status is-${qq.status}`}>{STATUS_LABEL[qq.status]}</span>
                      {qq.time_taken_seconds ? <span className="fan-q-time"><i className="far fa-clock" /> {qq.time_taken_seconds}s</span> : null}
                    </header>

                    <div className="fan-q-text"><Html html={qq.question} /></div>
                    {(qq.subject || qq.chapter || qq.topic) ? (
                      <div className="fan-q-tags">
                        {qq.subject ? <span className={`is-subject fdn-subj--${String(qq.subject).toLowerCase().replace(/[^a-z]/g, "")}`}>{qq.subject}</span> : null}
                        {qq.chapter ? <span>{qq.chapter}</span> : null}
                        {qq.topic ? <span>{qq.topic}</span> : null}
                      </div>
                    ) : null}

                    {this.renderOptions(qq.options, qq.correct_option, qq.selected_option)}

                    {qq.explanation ? (
                      <div className="fan-explain">
                        <h6><i className="fas fa-lightbulb" /> Explanation</h6>
                        <div><Html html={qq.explanation} /></div>
                      </div>
                    ) : null}

                    {qq.similar && qq.similar.length ? (
                      <div className="fan-similar">
                        <button className="fan-similar-toggle" onClick={() => this.toggleSimilar(qq.question_id)}>
                          <i className={`fas fa-chevron-${openSimilar[qq.question_id] ? "up" : "down"}`} />
                          {openSimilar[qq.question_id] ? "Hide" : "Show"} {qq.similar.length} similar question{qq.similar.length > 1 ? "s" : ""}
                        </button>
                        {openSimilar[qq.question_id] ? (
                          <div className="fan-similar-list">
                            {qq.similar.map((sq, i) => (
                              <div className="fan-sim" key={sq.question_id}>
                                <div className="fan-sim-no">Similar {i + 1}</div>
                                <div className="fan-q-text"><Html html={sq.question} /></div>
                                {this.renderOptions(sq.options, sq.answer, null)}
                                {sq.explanation ? (
                                  <div className="fan-explain small">
                                    <h6><i className="fas fa-lightbulb" /> Explanation</h6>
                                    <div><Html html={sq.explanation} /></div>
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>

              <footer className="fan-foot">
                <button className="fan-btn fdn-btn fdn-btn--secondary" onClick={onClose}>Close</button>
              </footer>
            </>
          )}
        </div>
      </div>
      </MathJaxContext>
    );
  }
}

const FoundationAttemptAnalysis = graphql(ATTEMPT_ANALYSIS, {
  name: "analysis",
  skip: (props) => !props.attemptId,
  options: (props) => ({
    variables: { attempt_id: Number(props.attemptId) },
    fetchPolicy: "cache-and-network",
  }),
})(FoundationAttemptAnalysisView);

export default FoundationAttemptAnalysis;
